import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { Header } from '@/components/shared/Header';
import { Footer } from '@/components/shared/Footer';
import { PageLayout } from '@/components/shared/PageLayout';
import { LanguageSelector } from '@/components/shared/LanguageSelector';
import { StatusCard } from '@/components/ui/card';
import { AuthLoginCard } from '@/features/auth/components/AuthLoginCard';
import { useTranslation } from '@/hooks/useTranslation';
import { useErrorHandler } from '@/hooks/useErrorHandler';

export function AuthPage() {
  const { login, tokenService, isAuthenticated, isAuthInitialized } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const { errorMessage, handleError, clearError } = useErrorHandler('AuthPage');
  const [rememberMe, setRememberMe] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!isAuthInitialized) return;

    const savedUser = tokenService?.getUserMail();
    if (savedUser) {
      setUsername(savedUser);
      setRememberMe(true);
    }

    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthInitialized, isAuthenticated, navigate, tokenService]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    clearError();
    setIsSubmitting(true);

    try {
      await login(username, password, rememberMe);
      navigate('/');
    } catch (err) {
      handleError(err, { username, rememberMe });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUsernameChange = (value: string) => {
    setUsername(value);
    if (errorMessage) {
      clearError();
    }
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    if (errorMessage) {
      clearError();
    }
  };

  if (!isAuthInitialized) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <PageLayout className="flex-1" contentClassName="flex flex-1 items-center justify-center">
          <StatusCard type="loading" message={t('auth.loading')} />
        </PageLayout>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <PageLayout
        className="flex-1"
        contentClassName="relative flex flex-1 flex-col items-center justify-center gap-5 py-12"
      >
        <div className="absolute top-0 right-0">
          <LanguageSelector />
        </div>

        <div className="flex flex-col items-center gap-2 text-center">
          <div className="flex items-center gap-3">
            <img src="/toollife-icon-tool.svg" alt={t('common.brand')} className="size-11" />
            <span className="text-2xl font-bold text-foreground">{t('common.brand')}</span>
          </div>
          <p className="text-sm text-muted-foreground tracking-wide">{t('auth.hero.tagline')}</p>
        </div>

        <div className="w-full max-w-[400px]">
          <AuthLoginCard
            username={username}
            password={password}
            rememberMe={rememberMe}
            isSubmitting={isSubmitting}
            isAuthInitialized={isAuthInitialized}
            error={errorMessage || undefined}
            onSubmit={handleSubmit}
            onUsernameChange={handleUsernameChange}
            onPasswordChange={handlePasswordChange}
            onRememberMeChange={setRememberMe}
          />
        </div>

        <p className="text-xs text-muted-foreground">
          {t('auth.support.title')}:{' '}
          <a
            href={`mailto:${t('auth.support.email')}`}
            className="text-muted-foreground hover:text-foreground hover:underline"
          >
            {t('auth.support.email')}
          </a>
        </p>
      </PageLayout>
      <Footer />
    </div>
  );
}
